import React, { useState, useEffect, Fragment } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';

// FORMATTED MESSAGES
import BulkSendFormattedMessages from '../../../messages/websms/BulkSendFormattedMessages';

// COMPONENTS
import Paginator from '../../common/Paginator';
import HelpTooltip from '../../common/HelpTooltip';

// UTILS
import { stripNonNumerical } from '../../../utils/validate';

// CONFIG
import config from '../../../Config';

export default function BulkSendTable({
  list,
  numberColumn,
  nameColumn,
  messageColumn,
  pageNumber,
  setPageNumber,
  changeEvent,
  selectRowsToBeDeleted,
  changePageFunction,
  rowsToBeDeleted,
}) {
  const [pageLength, setPageLength] = useState(config.defaultPL);

  useEffect(() => {
    if (changePageFunction) {
      changePageFunction(resetFunction);
    }
  }, [changePageFunction]);

  const resetFunction = (page) => {
    changeEvent(page);
  };

  // Changes the amount of entries per pagination page.
  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    changeEvent(1);
  };

  const generateTableBody = () => {
    return list
      .slice(
        (pageNumber - 1) * pageLength,
        (pageNumber - 1) * pageLength + pageLength
      )
      .map((element, i) => {
        let smsText = element.templateMessage || element[messageColumn] || '';

        // regex to check if there is a unicode character, therefore making it a unicode message
        let unicodeMessage = smsText.match(
          // eslint-disable-next-line no-control-regex
          /[^\u000A\u0020-\u007E]/g && /[^\x00-\x7F]/g
        );

        let messagePartCount = 1;
        if (unicodeMessage) {
          if (smsText.length > 70)
            messagePartCount = Math.ceil(smsText.length / 67);
        } else if (smsText.length > 160)
          messagePartCount = Math.ceil(smsText.length / 153);

        const textColor = element.valid === false ? 'red' : 'inherit';

        return (
          <TableRow key={`list-${i}`} id={`list-${i}`}>
            <TableCell sx={{ color: textColor }}>{element[0]}</TableCell>
            <TableCell sx={{ color: textColor }}>
              {stripNonNumerical(element[numberColumn])}
            </TableCell>
            <TableCell sx={{ color: textColor }}>
              {element[nameColumn]}
            </TableCell>
            <TableCell sx={{ color: textColor }}>{smsText}</TableCell>
            <TableCell sx={{ color: textColor }}>{messagePartCount}</TableCell>
            <TableCell sx={{ color: textColor }}>
              <Checkbox
                id={`recipient_${element[0]}`}
                name="delete"
                onChange={selectRowsToBeDeleted}
                checked={rowsToBeDeleted.includes(`recipient_${element[0]}`)}
                color="secondary"
              />
            </TableCell>
          </TableRow>
        );
      });
  };

  return (
    <Fragment>
      <TableContainer
        sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
      >
        <Table aria-label="Imported Recipients Table">
          <TableHead>
            <TableRow>
              <TableCell>{BulkSendFormattedMessages.hashColumn}</TableCell>
              <TableCell>
                {BulkSendFormattedMessages.recipientColumn}
                <HelpTooltip
                  message={
                    'Recipient number should only contain numbers and +\n' +
                    'eg: +0123456789/0123456789'
                  }
                />
              </TableCell>
              <TableCell>{BulkSendFormattedMessages.nameColumn}</TableCell>
              <TableCell>
                {BulkSendFormattedMessages.messageColumn}
                <HelpTooltip
                  message={
                    '1 message = 160 characters or less (including spaces and signature character count).\n' +
                    'If 160 characters is exceeded, the message will be broken into parts, consisting of 153 characters.\n' +
                    'We can support max. of 765(plain)/335(unicode) characters = 5 parts.\n' +
                    '1 unicode SMS = 70 unicode characters, any message with 1 or more unicode characters is considered a unicode message and will be counted as such. Emojis are counted as unicode characters'
                  }
                />
              </TableCell>
              <TableCell>{BulkSendFormattedMessages.countColumn}</TableCell>
              <TableCell>{BulkSendFormattedMessages.deleteRow}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{generateTableBody()}</TableBody>
        </Table>
      </TableContainer>

      <Paginator
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        itemCount={list.length}
        changeEvent={changeEvent}
        pageLength={pageLength}
        editFunction={(e) => changePageLength(e)}
      />
    </Fragment>
  );
}
